// src/pages/driver/DriverCreatePage.js
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Alert,
  Breadcrumbs,
  Link,
  Button,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getCompanies } from '../../api/companyAPI';
import { getCompanyDriverGroups } from '../../api/driverGroupAPI';
import { createDriver } from '../../api/driverAPI';
import DriverForm from '../../components/driver/DriverForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const DriverCreatePage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  
  // Check if a group ID is provided in URL params
  const groupIdFromUrl = searchParams.get('group');
  
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(groupIdFromUrl || '');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch companies for the dropdown
  const { data: companies, isLoading: isLoadingCompanies, isError: isCompaniesError, error: companiesError } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Set default company when companies load
  React.useEffect(() => {
    if (companies && companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].CompanyId.toString());
    }
  }, [companies, selectedCompanyId]);

  // Fetch driver groups for selected company
  const { data: driverGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['company-driver-groups', selectedCompanyId],
    queryFn: () => getCompanyDriverGroups(parseInt(selectedCompanyId)),
    enabled: !!selectedCompanyId
  });

  // Create driver mutation
  const mutation = useMutation({
    mutationFn: createDriver,
    onSuccess: (data) => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
      
      setSnackbar({
        open: true,
        message: 'Driver created successfully!',
        severity: 'success'
      });
      
      // Navigate to driver details page after creation
      setTimeout(() => {
        navigate(`/drivers/${data.DriverId}`);
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error creating driver: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
    setSelectedGroupId(''); // Reset group when company changes
  };

  const handleGroupChange = (event) => {
    setSelectedGroupId(event.target.value);
  };

  const handleSubmit = async (formData) => {
    // Validate required selections
    if (!selectedCompanyId) {
      setSnackbar({
        open: true,
        message: 'Please select a company',
        severity: 'error'
      });
      return;
    }

    if (!selectedGroupId) {
      setSnackbar({
        open: true,
        message: 'Please select a driver group',
        severity: 'error'
      });
      return;
    }
    
    try {
      // Combine form data with selected company and group IDs
      const driverData = {
        ...formData,
        DriverCompanyId: parseInt(selectedCompanyId),
        DriverGroupId: parseInt(selectedGroupId)
      };
      
      console.log('Submitting driver data:', driverData); // Debug log
      
      await mutation.mutateAsync(driverData);
    } catch (err) {
      console.error('Error creating driver:', err);
      // Error is handled by the mutation onError callback
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoadingCompanies) {
    return <LoadingSpinner />;
  }

  if (isCompaniesError) {
    return <ErrorAlert message={companiesError instanceof Error ? companiesError.message : 'Error loading companies'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4">Create New Driver</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/drivers" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Drivers
        </Button>
      </Box>

      {/* Company Selection */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Select Company</Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="company-select-label">Company</InputLabel>
          <Select
            labelId="company-select-label"
            value={selectedCompanyId}
            label="Company"
            onChange={handleCompanyChange}
            error={!selectedCompanyId}
          >
            <MenuItem value="" disabled>
              <em>Select a company</em>
            </MenuItem>
            {companies && companies.map((company) => (
              <MenuItem key={company.CompanyId} value={company.CompanyId.toString()}>
                {company.CompanyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Driver Group Selection - Required */}
      {selectedCompanyId && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Select Driver Group (Required)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every driver must be assigned to a driver group. The group determines the tariff that applies to this driver.
          </Typography>
          
          {isLoadingGroups ? (
            <LoadingSpinner />
          ) : (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="group-select-label">Driver Group *</InputLabel>
              <Select
                labelId="group-select-label"
                value={selectedGroupId}
                label="Driver Group *"
                onChange={handleGroupChange}
                error={!selectedGroupId}
                required
              >
                <MenuItem value="" disabled>
                  <em>Select a driver group</em>
                </MenuItem>
                {driverGroups && driverGroups.map((group) => (
                  <MenuItem key={group.DriversGroupId} value={group.DriversGroupId.toString()}>
                    {group.DriversGroupName} {group.DriverTariffId && `(Tariff ID: ${group.DriverTariffId})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {driverGroups && driverGroups.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No driver groups found for this company. 
              <Button 
                component={RouterLink} 
                to="/driver_groups/new" 
                sx={{ ml: 1 }}
              >
                Create a driver group first
              </Button>
            </Alert>
          )}
        </Paper>
      )}

      {/* Show driver form only if both company and group are selected */}
      {selectedCompanyId && selectedGroupId && (
        <>
          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {mutation.error?.message || 'An error occurred while creating the driver'}
            </Alert>
          )}
          
          <DriverForm 
            companyId={parseInt(selectedCompanyId)}
            onSubmit={handleSubmit} 
            isLoading={mutation.isPending}
          />
        </>
      )}

      {/* Show message if selections are incomplete */}
      {(!selectedCompanyId || !selectedGroupId) && selectedCompanyId && (
        <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Please select both a company and driver group to continue with driver creation.
          </Typography>
        </Paper>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverCreatePage;